import clsx from "clsx";
import { formatDate, isSameDay } from "date-fns";
import { numericFormatter } from "react-number-format";
import { useNavigate, useParams } from "react-router-dom";
import { useRestoreContactsMutation } from "../../../../../../features/contactsApi";
import { addBannerWithTimeout } from "../../../../../lib/centralizedBanner/slice";
import { ContactDTO, ContactType, FindAllContactsFilters, FindContactsTargetResponse } from "../../../../../lib/filters/dto";
import { keycloak } from "../../../../../lib/keycloak";
import { useFindAllPreferencesQuery, useUpdatePreferencesMutation } from "../../../../../lib/preferences/api";
import { targetListCompanyColumns } from "../../../../../lib/preferences/dto";
import { useAppDispatch, useAppSelector } from "../../../../../lib/redux/hooks";
import { colors } from "../../../../../ui/colors";
import { EditIcon } from "../../../../../ui/icons/edit";
import { GlobeIcon } from "../../../../../ui/icons/globe";
import { MailIcon } from "../../../../../ui/icons/mail";
import { TrashIcon } from "../../../../../ui/icons/trash";
import { UserPlusIcon } from "../../../../../ui/icons/userPlus";
import { UsersIcon } from "../../../../../ui/icons/users";
import { Badge } from "../../../../../ui/molecules/badge";
import Button from "../../../../../ui/molecules/button";
import Checkbox from "../../../../../ui/molecules/checkbox";
import { Spinner } from "../../../../../ui/molecules/spinner";
import { Dropdown } from "../../../../../ui/organisms/dropdown";
import { CustomTable } from "../../../../../ui/organisms/table";
import { buildAddress } from "../../../../database/companies/list/table";
import { EmailsMessagesDTO } from "../../../../projects/mails/dto";
import { CustomerAccessEnum } from "../../../../users/dto";
import { ChangeStatusTooltip } from "../../../contacts/changeStatusTooltip";
import { CreateNoteTooltip } from "../../../contacts/createNoteTooltip";
import { Favorite } from "../../../contacts/updateFavorite";
import { ContactStateEnum, NoteDTO } from "../../../dto";
import { PersonDTO } from "../../../../database/people/dto";

interface Props {
    contacts: {
        total: number
        page: number
        perPage: number
        prev: boolean
        next: boolean
        totalPage: number
        data: { company: ContactDTO<ContactType.COMPANY>, employees: PersonDTO[] }[]
    }
    selectedIds: { contactId: string, companyId: string }[]
    setSelectedIds: (arg: { contactId: string, companyId: string }[]) => void
    setShowPopup: (arg: boolean) => void
    filters: FindAllContactsFilters
    setFilters: (arg: Partial<FindAllContactsFilters>) => void
    isLoadingEmailsHistory: boolean
    emailsHistory: { messages: EmailsMessagesDTO[] }
}

export function TargetCompaniesTable(props: Props) {
    const targetId = useParams().targetId as string;
    const { selectedIds, setSelectedIds, setShowPopup, setFilters, filters, contacts, isLoadingEmailsHistory, emailsHistory } = props
    const usersState = useAppSelector(state => state.users)
    const dispatch = useAppDispatch()
    const navigate = useNavigate();

    const { data: preferencesResponse } = useFindAllPreferencesQuery(
        keycloak.subject!,
        { skip: !keycloak.subject }
    );
    const [updatePreferencesMutation] = useUpdatePreferencesMutation();
    const [restoreContacts] = useRestoreContactsMutation();

    const handleRestoreContacts = async (ids: string[]) => {
        try {
            await restoreContacts({ contacts: ids, targetList: targetId }).unwrap();
            dispatch(addBannerWithTimeout({
                message: 'Contatti ripristinati con successo.',
                variant: 'success'
            }));
        } catch (error) {
            dispatch(addBannerWithTimeout({
                message: 'Si è verificato un errore durante il ripristino dei contatti.',
                variant: 'error'
            }));
        }
    }

    const getColumnWidth = (column: string) => {
        return preferencesResponse?.data[0]?.targetListCompanyPreferences?.find(
            col => col.column === column
        )?.width ?? 150;
    };

    function lastNote(notes: NoteDTO[] | null): string {
        if (!notes || notes.length <= 0) {
            return '-'
        } if (notes && notes.length > 0) {
            return notes[notes?.length - 1].note
        }
        else return '-'
    }

    return (
        <CustomTable
            asc={filters.order}
            settedOrder={filters.sort}
            tableHeight={
                keycloak.hasRealmRole('customer') && usersState.findMeResponse?.customerAccess !== CustomerAccessEnum.FULL ?
                    '100%' :
                    filters.outcome.map(v => v.value).includes(ContactStateEnum.TORECALL) ? 'calc(100% - 84px)' : 'calc(100% - 48px)'
            }
            prevOrderHeads={[
                <th className="text-left w-10">
                    <Checkbox
                        shape={"square"}
                        checked={
                            selectedIds.length ===
                            contacts.data.length
                        }
                        onCheck={function (value: boolean): void {
                            if (
                                selectedIds.length ===
                                contacts.data.length
                            ) {
                                setSelectedIds([]);
                            } else {
                                setSelectedIds(
                                    contacts.data?.map(
                                        (company) => ({ contactId: company.company.id, companyId: company.company.contact.id })
                                    )!
                                );
                            }
                        }}
                    />
                </th>
            ]}
            postOrderHeads={[
                <th className="text-left">
                    <div className="flex gap-[4px]">
                    </div>
                </th>,
                // <th className="text-left">
                //     <div className="flex gap-[4px] w-[20px]" />
                // </th>
            ]}
            preferences={preferencesResponse?.data[0]?.targetListCompanyPreferences?.map(pref => ({ ...pref, order: targetListCompanyColumns.find(col => col.column === pref.column)?.order })) ?? []}
            setPreferences={(items) => {
                updatePreferencesMutation({
                    data: {
                        owner: preferencesResponse?.data[0]?.owner!,
                        personPreferences: preferencesResponse?.data[0]?.personPreferences!,
                        companyPreferences: preferencesResponse?.data[0]?.companyPreferences!,
                        targetListPreferences: preferencesResponse?.data[0]?.targetListPreferences!,
                        targetListCompanyPreferences: items,
                        targetListPersonPreferences: preferencesResponse?.data[0]?.targetListPersonPreferences!,
                        toVerifyBinPersonPreferences: preferencesResponse?.data[0]?.toVerifyBinPersonPreferences!,
                        toVerifyBinCompanyPreferences: preferencesResponse?.data[0]?.toVerifyBinCompanyPreferences!,
                    },
                    id: preferencesResponse?.data[0]?.id!,
                });
            }}
            setOrderFilters={(order) => {
                setFilters({ order: !filters.order, sort: order })
            }}
            rows={contacts.data.map((d, index) => {
                let emailsToCheck: string[] = []
                emailsToCheck = [...emailsToCheck, ...(d.employees?.flatMap(employee => employee.emails) || [])]
                emailsToCheck = [...emailsToCheck, ...(d.company.contact.emails || [])]
                emailsToCheck = [...emailsToCheck, ...(d.company.sendEmails?.map(email => email) || [])]

                return ({
                    key: d.company.id,
                    highlight: isSameDay(new Date(d.company.lastContactDate!), new Date()),
                    // highlightPulse: _contactsState.listIndexToHighlight === index,
                    data: [
                        {
                            item: <td className="text-text-sm font-semibold text-neutral-600">
                                <Checkbox
                                    shape={"square"}
                                    checked={selectedIds.map(id => id.contactId).includes(d.company.id)}
                                    onCheck={function (value: boolean): void {
                                        if (selectedIds.map(id => id.contactId).includes(d.company.id)) {
                                            setSelectedIds([
                                                ...selectedIds.filter((id) => id.contactId !== d.company.id),
                                            ]);
                                        } else {
                                            setSelectedIds([...selectedIds, { contactId: d.company.id, companyId: d.company.contact.id }]);
                                        }
                                    }}
                                />
                            </td>,
                            group: 'preSort',
                            key: 'checkbox'
                        },
                        {
                            item: <td
                                className={d.company.contact.callsCounter >= 3 ? "blur-[2.5px]" : ""}
                                onClick={() => {
                                    navigate(`${window.location.pathname + '/' + d.company.contact.id}/form/${d.company.id}`)
                                }}
                            >
                                <div
                                    className="cursor-pointer text-text-sm crop-text font-semibold text-brandPrimary-600"
                                    style={{ width: getColumnWidth('Nome azienda') }}
                                >
                                    {d.company.contact.name}
                                </div>
                            </td>,
                            group: 'sort',
                            key: 'Nome azienda'
                        },
                        {
                            item: <td className={d.company.contact.callsCounter >= 3 ? "blur-[2.5px] pointer-events-none" : "relative group"}>
                                {d.company.contact.particularities && d.company.contact.particularities.length > 0 ? (
                                    <div className="text-text-sm font-semibold text-neutral-600 crop-text" style={{ width: getColumnWidth('Particolarità') ?? 100 }}>
                                        {d.company.contact.particularities ? (d.company.contact.particularities[0] + (d.company.contact.particularities.length > 1 ? (', ..., + ' + (d.company.contact.particularities.length - 1)) : '')) : '-'}
                                    </div>
                                ) : (
                                    "-"
                                )}
                                {d.company.contact.particularities && d.company.contact.particularities.length > 0 && <div className="absolute left-0 bottom-full z-50 hidden group-hover:block text-neutral-700 bg-neutral-200 rounded-md p-1 text-text-xs">
                                    {d.company.contact.particularities.map((item, index) => (
                                        <span key={index}>
                                            {item}
                                            <br />
                                        </span>
                                    ))}
                                </div>}
                            </td>,
                            group: 'sort',
                            key: 'Particolarità'
                        },
                        {
                            item: <td className={d.company.contact.callsCounter >= 3 ? "blur-[2.5px] pointer-events-none" : ""}>
                                <div
                                    className="text-text-sm crop-text text-neutral-600" style={{ width: getColumnWidth('Data di contatto') }}
                                >
                                    {d.company.lastContactDate ? formatDate(new Date(d.company.lastContactDate!), 'dd/MM/yyyy HH:mm') : "-"}
                                </div>
                            </td>,
                            group: 'sort',
                            key: "Data di contatto"
                        },
                        {
                            item: <td className={d.company.contact.callsCounter >= 3 ? "blur-[2.5px] pointer-events-none" : ""}>
                                {d.company.contact.phones ? (
                                    <a
                                        className={d.company.contact.callsCounter >= 3 ? "blur-[2.5px] pointer-events-none" : ""}
                                        rel="noreferrer"
                                        target="_blank"
                                        href={"tel:" + d.company.contact.phones[0]?.phone}
                                    >
                                        <div className="text-text-sm font-semibold text-neutral-600 crop-text" style={{ width: getColumnWidth('Telefono') }}>
                                            {d.company.contact.phones ? (d.company.contact.phones[0]?.phone + (d.company.contact.phones.length > 1 ? (', ..., + ' + (d.company.contact.phones.length - 1)) : '')) : '-'}
                                        </div>
                                    </a>
                                ) : (
                                    "-"
                                )}
                            </td>,
                            group: 'sort',
                            key: 'Telefono'
                        },
                        {
                            item: <td className={d.company.contact.callsCounter >= 3 ? "blur-[2.5px]" : ""}>
                                <ChangeStatusTooltip
                                    disabled={d.company.contact.callsCounter >= 3}
                                    contact={d.company}
                                    noResponseCount={d.company.noResponseCount}
                                    width={getColumnWidth('Esito')}
                                    contactId={d.company.id}
                                    outcome={d.company.outcome}
                                    info={d.company.outcomeInfo}
                                    contactType={ContactType.COMPANY}
                                    listIndex={index}
                                />
                            </td>,
                            group: 'sort',
                            key: 'Esito'
                        },
                        {
                            item: <td className={d.company.contact.callsCounter >= 3 ? "blur-[2.5px] pointer-events-none" : "relative group"}>
                                {d.company.contact.particularities && d.company.contact.particularities.length > 0 ? (
                                    <div className="text-text-sm font-semibold text-neutral-600 crop-text" style={{ width: getColumnWidth('Particolarità') }}>
                                        {d.company.contact.particularities ? (d.company.contact.particularities[0] + (d.company.contact.particularities.length > 1 ? (', ..., + ' + (d.company.contact.particularities.length - 1)) : '')) : '-'}
                                    </div>
                                ) : (
                                    "-"
                                )}
                                {d.company.contact.particularities && d.company.contact.particularities.length > 0 && <div className="absolute left-0 bottom-full z-50 hidden group-hover:block text-neutral-700 bg-neutral-200 rounded-md p-1 text-text-xs">
                                    {d.company.contact.particularities.map((item, index) => (
                                        <span key={index}>
                                            {item + ","}
                                            <br />
                                        </span>
                                    ))}
                                </div>}
                            </td>,
                            group: 'sort',
                            key: 'Particolarità'
                        },
                        {
                            item: <td className={d.company.contact.callsCounter >= 3 ? "blur-[2.5px] pointer-events-none" : ""}>
                                <CreateNoteTooltip
                                    targetListId={targetId}
                                    notes={d.company.notes}
                                    contactId={d.company.id}
                                    contactType={ContactType.COMPANY}
                                    width={getColumnWidth('Note')}
                                    lastNote={lastNote(d.company.notes)}
                                />
                            </td>,
                            group: 'sort',
                            key: 'Note'
                        },
                        {
                            item: <td className={d.company.contact.callsCounter >= 3 ? "blur-[2.5px] pointer-events-none" : ""}>
                                {d.company.contact.emails ? (
                                    <a
                                        rel="noreferrer"
                                        target="_blank"
                                        href={"mailto:" + d.company.contact.emails}
                                    >
                                        <div className="text-text-sm font-semibold text-neutral-600 crop-text" style={{ width: getColumnWidth('Email') }}>
                                            {d.company.contact.emails && d.company.contact.emails[0] ? (d.company.contact.emails[0] + (d.company.contact.emails.length > 1 ? (', ..., + ' + (d.company.contact.emails.length - 1)) : '')) : '-'}
                                        </div>
                                    </a>
                                ) : (
                                    "-"
                                )}
                            </td>,
                            group: 'sort',
                            key: 'Email'
                        },
                        {
                            item: <td className={d.company.contact.callsCounter >= 3 ? "blur-[2.5px] pointer-events-none" : ""}>
                                <div
                                    className="px-[4px] crop-text text-text-sm font-semibold text-neutral-600" style={{ width: getColumnWidth('Dipendenti') }}
                                >
                                    {d.company.contact.employeesNumber !== null ? numericFormatter(d.company.contact.employeesNumber.toString() ?? "0", { thousandSeparator: '.' }) : "-"}
                                </div>
                            </td>,
                            group: 'sort',
                            key: 'Dipendenti'
                        },
                        {
                            item: <td className={d.company.contact.callsCounter >= 3 ? "blur-[2.5px] pointer-events-none" : ""}>
                                <div
                                    className="px-[4px] crop-text text-text-md font-semibold text-neutral-600" style={{ width: getColumnWidth('P. IVA') }}
                                >
                                    {d.company.contact.vat ?? "-"}
                                </div>
                            </td>,
                            group: 'sort',
                            key: 'P. IVA'
                        },
                        {
                            item: <td className={d.company.contact.callsCounter >= 3 ? "blur-[2.5px] pointer-events-none" : ""}>
                                <div
                                    className="crop-text text-text-sm font-semibold text-neutral-600" style={{ width: getColumnWidth('Sede azienda') }}
                                >
                                    {buildAddress(d.company.contact.address, d.company.contact.city, d.company.contact.countryRegion, d.company.contact.state, d.company.contact.zipCode)}
                                </div>
                            </td>,
                            group: 'sort',
                            key: 'Sede azienda'
                        },
                        {
                            item: <td className={d.company.contact.callsCounter >= 3 ? "blur-[2.5px] pointer-events-none" : ""}>
                                <a
                                    rel="noreferrer"
                                    target="_blank"
                                    href={d.company.contact.website ?? undefined}
                                    className="flex items-center justify-center"
                                >
                                    <GlobeIcon
                                        color={
                                            d.company.contact.website
                                                ? colors.neutral[400]
                                                : colors.neutral[200]
                                        }
                                        size={20}
                                    />
                                </a>
                            </td>,
                            group: 'sort',
                            key: 'Sito web'
                        },
                        {
                            item: <td className={d.company.contact.callsCounter >= 3 ? "blur-[2.5px] pointer-events-none" : ""}>
                                <div
                                    className="px-[4px] crop-text text-text-md font-semibold text-neutral-600" style={{ width: getColumnWidth('Cod. ATECO') }}
                                >
                                    {d.company.contact.ateco ? d.company.contact.ateco.atecoCode : "-"}
                                </div>
                            </td>,
                            group: 'sort',
                            key: 'Cod. ATECO'
                        },
                        {
                            item: <td className={d.company.contact.callsCounter >= 3 ? "blur-[2.5px] pointer-events-none" : ""}>
                                <div
                                    className="text-text-sm crop-text text-neutral-600" style={{ width: getColumnWidth('Fatturato') }}
                                >
                                    {d.company.contact.revenue !== null ? numericFormatter(d.company.contact.revenue.toString(), { thousandSeparator: '.', decimalSeparator: ',', fixedDecimalScale: true, decimalScale: 2, prefix: '€ ' }) : '-'}
                                </div>
                            </td>,
                            group: 'sort',
                            key: 'Fatturato'
                        },
                        {
                            item: <td className={d.company.contact.callsCounter >= 3 ? "blur-[2.5px] pointer-events-none" : ""}>
                                <div
                                    className="px-[4px] crop-text text-text-md font-semibold text-neutral-600" style={{ width: getColumnWidth('Settore') }}
                                >
                                    {d.company.contact.ateco ? d.company.contact.ateco.sector : "-"}
                                </div>
                            </td>,
                            group: 'sort',
                            key: 'Settore'
                        },
                        {
                            item: <td className={d.company.contact.callsCounter >= 3 ? "blur-[2.5px] pointer-events-none" : ""}>
                                <div
                                    className="px-[4px] crop-text text-text-md font-semibold text-neutral-600" style={{ width: getColumnWidth('CAP') }}
                                >
                                    {d.company.contact.zipCode ?? "-"}
                                </div>
                            </td>,
                            group: 'sort',
                            key: 'CAP'
                        },
                        {
                            item: <td className={d.company.contact.callsCounter >= 3 ? "blur-[2.5px] pointer-events-none" : ""}>
                                <div className="px-[4px] crop-text text-text-md font-semibold text-neutral-600" style={{ width: getColumnWidth('Categoria') }}>
                                    {d.company.contact.ateco ? d.company.contact.ateco.category : "-"}
                                </div>
                            </td>,
                            group: 'sort',
                            key: 'Categoria'
                        },
                        {
                            item: <td className="">
                                <div className="flex items-center justify-center gap-2">
                                    <div className={clsx("relative", { hidden: keycloak.hasRealmRole('customer') && usersState.findMeResponse?.customerAccess !== CustomerAccessEnum.FULL })}>
                                        {
                                            d.company.contact.employeesId && d.company.contact.employeesId.length > 0 &&
                                            <div className="h-4 min-w-4 rounded-full px-2 bg-brandPrimary-500 absolute left-1/2 text-label-xs flex items-center justify-center text-white" >
                                                {d.company.contact.employeesId.length}
                                            </div>
                                        }

                                        <Button
                                            icon={<UsersIcon color={""} size={0} />}
                                            size={"md"}
                                            iconPosition={"only"}
                                            disabled={!d.company.contact.employeesId || d.company.contact.employeesId.length === 0}
                                            variant={"link"}
                                            color={"gray"}
                                            onClick={() => {
                                                navigate(`${window.location.pathname + '/' + d.company.contact.id}/employees/${d.company.id}`)
                                            }}
                                        />
                                    </div>
                                    <div className="relative">
                                        {
                                            d.company.notes && d.company.notes.length > 0 &&
                                            <div className="h-4 min-w-4 rounded-full px-2 bg-brandPrimary-500 absolute left-1/2 text-label-xs flex items-center justify-center text-white" >
                                                {d.company.notes.length}
                                            </div>
                                        }
                                        <Button
                                            icon={<EditIcon color={""} size={0} />}
                                            size={"md"}
                                            iconPosition={"only"}
                                            disabled={!d.company.notes || d.company.notes.length === 0}
                                            variant={"link"}
                                            color={"gray"}
                                            onClick={() => {
                                                navigate(`${window.location.pathname + '/' + d.company.contact.id}/notes/${d.company.id}`)
                                            }}
                                        />
                                    </div>
                                    <div className="relative">
                                        <Button
                                            icon={<MailIcon color={""} size={0} />}
                                            size={"md"}
                                            iconPosition={"only"}
                                            variant={"link"}
                                            color={"gray"}
                                            onClick={() => {
                                                navigate(`${window.location.pathname + '/' + d.company.contact.id}/emails/${d.company.id}`)
                                            }}
                                        />
                                        <div
                                            className={clsx("absolute top-0 right-[0%]", {
                                                hidden: emailsHistory?.messages?.flatMap(item => item).filter(item => emailsToCheck.includes(item.to_email)).length === 0
                                            })}
                                        >
                                            {
                                                isLoadingEmailsHistory ?
                                                    <Spinner size={10} /> :
                                                    <Badge
                                                        color={
                                                            (emailsHistory?.messages?.flatMap(item => item).filter(item => item.opens_count && emailsToCheck.includes(item.to_email)).length > 0) ? 'green' : "yellow"}
                                                        label={''}
                                                    />
                                            }
                                        </div>
                                    </div>
                                    {!keycloak.hasRealmRole('customer') && <Favorite contactId={d.company.id} favorite={d.company.isFavorite} />}
                                    <div className="w-[20px] flex items-center justify-center cursor-pointer">
                                        <Dropdown
                                            items={[
                                                {
                                                    label: "Modifica",
                                                    icon: <EditIcon color={""} size={0} />,
                                                    onClick: () => {
                                                        navigate(`${window.location.pathname + '/' + d.company.contact.id}/employees/${d.company.id}`)
                                                    },
                                                },
                                                (!(keycloak.hasRealmRole('customer') || keycloak.hasRealmRole('collaborator')) &&
                                                    d.company.deletedAt !== null ?
                                                    {
                                                        label: "Ripristina",
                                                        icon: <UserPlusIcon color={""} size={0} />,
                                                        onClick: () => {
                                                            handleRestoreContacts([d.company.id]);
                                                        },
                                                    } : null),
                                                ((keycloak.hasRealmRole('commercial') || keycloak.hasRealmRole('customer') || keycloak.hasRealmRole('collaborator')) ? null : {
                                                    label: "Cancella",
                                                    icon: <TrashIcon color={""} size={0} />,
                                                    onClick: () => {
                                                        setSelectedIds([{ contactId: d.company.contact.id, companyId: d.company.id }]);
                                                        setShowPopup(true);
                                                    },
                                                }),
                                            ]}
                                        />
                                    </div>
                                </div>

                            </td>,
                            group: 'postSort',
                            key: 'employees'
                        },
                        // {
                        //     item: <td className="w-[20px] cursor-pointer">
                        //         <Dropdown
                        //             items={[
                        //                 {
                        //                     label: "Modifica",
                        //                     icon: <EditIcon color={""} size={0} />,
                        //                     onClick: () => {
                        //                         dispatch(setTabValueDetailsCompany(0))
                        //                         dispatch(setSelectedCompany(d.contact.id))
                        //                         dispatch(setSelectedContact(d.id))
                        //                         dispatch(findEmployees(d.contact.employeesId));
                        //                         dispatch(
                        //                             setSelectedCompanyNameForEmployees(d.contact.name)
                        //                         );
                        //                     },
                        //                 },
                        //                 (!(keycloak.hasRealmRole('customer') || keycloak.hasRealmRole('collaborator')) &&
                        //                     d.deletedAt !== null ?
                        //                     {
                        //                         label: "Ripristina",
                        //                         icon: <UserPlusIcon color={""} size={0} />,
                        //                         onClick: () => {
                        //                             dispatch(restoreContacts([d.id]))
                        //                         },
                        //                     } : null),
                        //                 ((keycloak.hasRealmRole('customer') || keycloak.hasRealmRole('collaborator')) ? null : {
                        //                     label: "Cancella",
                        //                     icon: <TrashIcon color={""} size={0} />,
                        //                     onClick: () => {
                        //                         setSelectedIds([d.id]);
                        //                         setShowPopup(true);
                        //                     },
                        //                 }),
                        //             ]}
                        //         />
                        //     </td>,
                        //     group: 'postSort',
                        //     key: 'menu'
                        // }
                    ]
                })
            }) ?? []}
        />
    )
}